import {
  Button,
  EmptyState,
  ListSkeleton,
  PageHeaderSkeleton,
  SkeletonRegion,
  toast,
} from '@nexus/ui';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../api/client';

/** Thrown by routes the person may not open. The API refuses the data anyway. */
export class ForbiddenError extends Error {
  constructor() {
    super('Forbidden');
    this.name = 'ForbiddenError';
  }
}

function HomeButton() {
  const { t } = useTranslation();
  return (
    <Button asChild variant="primary" icon="house">
      <a href="/">{t('errors.toHome')}</a>
    </Button>
  );
}

export function NotFoundPage() {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-[60dvh] items-center justify-center">
      <EmptyState
        icon="map"
        title={t('errors.notFoundTitle')}
        description={t('errors.notFoundDescription')}
        action={<HomeButton />}
      />
    </div>
  );
}

export function ForbiddenPage() {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-[60dvh] items-center justify-center">
      <EmptyState
        icon="lock"
        title={t('errors.forbiddenTitle')}
        description={t('errors.forbiddenDescription')}
        action={<HomeButton />}
      />
    </div>
  );
}

/**
 * Last resort when a page throws. Details go to the console only, never into the page. A server
 * error shows its error ID, which the support finds in the log.
 */
export function CrashPage({ error }: { error: unknown }) {
  const { t } = useTranslation();
  // eslint-disable-next-line no-console -- the only place unexpected render errors are reported
  console.error(error);
  const errorId = error instanceof ApiError ? error.errorId : undefined;
  return (
    <div className="flex min-h-[60dvh] items-center justify-center">
      <EmptyState
        icon="triangle-alert"
        title={t('errors.crashTitle')}
        description={
          <>
            {t('errors.crashDescription')}
            {errorId && (
              <span className="mt-2 block">
                {t('errors.crashErrorId')}{' '}
                <span className="font-mono font-medium text-fg select-all">{errorId}</span>
              </span>
            )}
          </>
        }
        action={
          <div className="flex flex-wrap justify-center gap-2">
            <Button variant="primary" icon="rotate-ccw" onClick={() => window.location.reload()}>
              {t('errors.reload')}
            </Button>
            {errorId && (
              <Button
                icon="copy"
                onClick={() =>
                  void navigator.clipboard
                    .writeText(errorId)
                    .then(() => toast.success(t('errors.errorIdCopied')))
                }
              >
                {t('errors.copyErrorId')}
              </Button>
            )}
          </div>
        }
      />
    </div>
  );
}

/** Placeholder while a page loads its code or data. */
export function PagePending() {
  return (
    <SkeletonRegion>
      <PageHeaderSkeleton breadcrumbs actions={1} />
      <ListSkeleton rows={5} title={false} trailing />
    </SkeletonRegion>
  );
}
